
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { FaQuoteRight } from "react-icons/fa";



const reviews = [
  {
    text: "KeyMech transformed our digital presence with a sleek, modern website and a solid marketing strategy. Their team is highly skilled and genuinely concerned about delivering results. We've seen a noticeable boost in leads since working with them.",
    name: "Alex",
   
    avatar: "A",
  },
  {
    text: "The creativity and professionalism of KeyMech are unmatched. They took the time to understand our brand and delivered a website that exceeded our expectations. The attention to detail is what makes them stand out.",
    name: "Sam ",

    avatar: "S",
  },
  {
    text: "Working with KeyMech has been an absolute pleasure. They delivered beyond our expectations and continue to support us. Highly recommend for any product development needs.",
    name: "Daniel",
  
    avatar: "D",
  },
];

function ClientReviews() {
  const [hoveredReview, setHoveredReview] = useState(null);

  return (
    <section className="py-16 sm:py-20 lg:py-24 xl:py-28 bg-white">
      <div className="max-w-full mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-28">
        <div className="text-center mb-10 sm:mb-14">
           
          <h2 className="text-[clamp(28px,3.5vw,52px)] font-bold text-[#1a1a1a]">
            <span className="text-[#c021d6]">Clients</span> Words
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {reviews.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ y: -12, scale: 1.02 }}
              onMouseEnter={() => setHoveredReview(i)}
              onMouseLeave={() => setHoveredReview(null)}
              className="bg-gray-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 flex flex-col gap-5 sm:gap-6 hover:shadow-xl hover:shadow-purple-50 transition-all duration-300 group"
            >
              <motion.p
                className="text-gray-600 text-sm sm:text-[15px] leading-relaxed flex-1"
                animate={{ color: hoveredReview === i ? "#4a1d6d" : "#4b5563" }}
              >
                "{r.text}"
              </motion.p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#c021d6] text-white flex items-center justify-center font-bold text-xs sm:text-sm"
                    animate={{
                      scale: hoveredReview === i ? 1.1 : 1,
                      backgroundColor: hoveredReview === i ? "#a018b5" : "#c021d6",
                    }}
                  >
                    {r.avatar}
                  </motion.div>
                  <div>
                    <p className="font-semibold text-[#1a1a1a] text-sm">{r.name}</p>
                    <p className="text-gray-400 text-xs">{r.role}</p>
                  </div>
                </div>
                <motion.div
                  animate={{
                    rotate: hoveredReview === i ? 360 : 0,
                    color: hoveredReview === i ? "#c021d6" : "#e5d9ff",
                  }}
                  transition={{ duration: 0.4 }}
                >
                  <FaQuoteRight className="text-primary/30 group-hover:text-primary/60 transition-colors text-xl sm:text-2xl" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ==================== FAQ ====================
const faqs = [
  { q: "What services do you offer?", a: "We offer PCB design, firmware development, industrial design, 3D printing, IoT integration, and full product engineering from concept to production." },
  { q: "How long does it take to complete a project?", a: "Project timelines vary depending on complexity. Simple prototypes may take 2–4 weeks, while full production-ready products typically take 3–6 months." },
  { q: "Can you work remotely or on-site?", a: "We can work both remotely and on-site. Our team is distributed but we can travel for key milestones." },
  { q: "Do you provide ongoing support after the project is complete?", a: "Yes, we offer post-launch support packages including bug fixes, updates, and feature enhancements." },
  { q: "What industries do you serve?", a: "We serve consumer electronics, medical devices, automotive, aerospace, and industrial automation sectors." },
  { q: "How do I get started?", a: "Simply contact us through our website or email. We'll schedule a free consultation to discuss your project needs." },
];

function FAQ() {
  const [open, setOpen] = useState(null);
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const Item = ({ faq, index }) => (
    <div className="border-b border-gray-100 py-4">
      <button
        className="w-full flex items-center justify-between text-left gap-4"
        onClick={() => setOpen(open === index ? null : index)}
      >
        <span className="font-medium text-[#1a1a1a] text-sm sm:text-[15px] xl:text-base">{faq.q}</span>
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#c021d6] flex items-center justify-center transition-transform ${
            open === index ? "bg-[#c021d6]" : ""
          }`}
        >
          <svg
            className={`w-3 h-3 transition-colors ${open === index ? "text-white" : "text-[#c021d6]"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d={open === index ? "M20 12H4" : "M12 4v16M4 12h16"}
            />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {open === index && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-gray-500 text-sm sm:text-[15px] mt-3 overflow-hidden"
          >
            {faq.a}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <section className="py-16 sm:py-20 lg:py-24 xl:py-28 bg-gray-50">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-28">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#c021d6] mb-3">↑ FAQ</p>
          <h2 className="text-[clamp(28px,3.5vw,52px)] font-bold text-[#1a1a1a]">
            Frequently <span className="text-[#c021d6]">Asked</span> Questions!
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16 xl:gap-x-24">
          <div>{left.map((faq, i) => <Item key={i} faq={faq} index={i} />)}</div>
          <div>{right.map((faq, i) => <Item key={i + half} faq={faq} index={i + half} />)}</div>
        </div>
      </div>
    </section>
  );
}

// ==================== CTA BANNER ====================
export function CTABanner() {
  return (
    <section className="py-16 sm:py-20 lg:py-24 xl:py-28 bg-white text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto px-4 sm:px-8"
      >
        <h2 className="text-[clamp(28px,3.5vw,52px)] font-bold text-[#1a1a1a] mb-6">
          How can we help you{" "}
          <span className="text-[#c021d6]">succeed?</span>
        </h2>
        <a
          href="#"
          className="inline-block bg-[#c021d6] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-purple-700 transition-colors shadow-lg shadow-purple-200 text-sm sm:text-base"
        >
          Find Your Solution
        </a>
      </motion.div>
    </section>
  );
}


export default ClientReviews;